import { toDash } from '../utils/string';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupParallelPlugin from '@swup/parallel-plugin';

import Swup from 'swup';
import { Scroll } from './Scroll';
import { SectionLinks } from './SectionLinks';
import { PlaylistProximity } from './PlaylistProximity';
import { CardSlider } from './CardSlider';
import { CSS_CLASS, CUSTOM_EVENT } from '../config';
import { $html } from '../utils/dom';

export class Load {
  constructor() {
    this.onVisitStartBind = this.onVisitStart.bind(this);
    this.beforeContentReplaceBind = this.beforeContentReplace.bind(this);
    this.onContentReplaceBind = this.onContentReplace.bind(this);
    this.onAnimationInEndBind = this.onAnimationInEnd.bind(this);
    this.onAnimationOutStartBind = this.onAnimationOutStart.bind(this);
    this.goTo = this.goTo.bind(this);
  }

  // =============================================================================
  // Lifecycle
  // =============================================================================
  init() {
    this.initSwup();
    this.bindEvents();
    this.updateActiveLinks();

    requestAnimationFrame(() => {
      $html.classList.add(CSS_CLASS.READY);
    });
  }

  destroy() {
    this.unbindEvents();

    this.swup?.destroy();
  }

  // =============================================================================
  // Events
  // =============================================================================
  bindEvents() {
    window.addEventListener(CUSTOM_EVENT.GO_TO, this.goTo);

    this.swup.hooks.on('visit:start', this.onVisitStartBind);
    this.swup.hooks.before('content:replace', this.beforeContentReplaceBind);
    this.swup.hooks.on('content:replace', this.onContentReplaceBind);
    this.swup.hooks.on('animation:in:end', this.onAnimationInEndBind);
    this.swup.hooks.on('animation:out:start', this.onAnimationOutStartBind);

    this.swup.hooks.on('fetch:error', e => {
      console.log('fetch:error:', e);
      debugger;
    });
    this.swup.hooks.on('fetch:timeout', e => {
      console.log('fetch:timeout:', e);
      debugger;
    });
  }

  unbindEvents() {
    window.removeEventListener(CUSTOM_EVENT.GO_TO, this.goTo);
  }

  // =============================================================================
  // Methods
  // =============================================================================
  initSwup() {
    this.swup = new Swup({
      animateHistoryBrowsing: true,
      plugins: this._getSwupPlugins(),
      // Игнорируем ссылки с data-no-swup
      ignoreVisit: (url, { el } = {}) => {
        // Игнорируем элементы с data-no-swup
        if (el?.hasAttribute('data-no-swup')) {
          return true;
        }
        return false;
      }
    });

    // Делаем Swup доступным глобально для компонентов
    window.swup = this.swup;
  }

  /**
   * Retrieve HTML dataset on next container and update our real html element dataset accordingly
   *
   * @param visit
   */
  updateDocumentAttributes(visit) {
    const parser = new DOMParser();
    const nextDOM = parser.parseFromString(visit.to.html, 'text/html');
    const newDataset = {
      ...nextDOM.querySelector('html')?.dataset
    };

    Object.entries(newDataset).forEach(([key, val]) => {
      $html.setAttribute(`data-${toDash(key)}`, val ?? '');
    });
  }

  /**
   * Добавляет класс `.is-active` для <a>, который ведёт на текущую страницу.
   * Трогает ТОЛЬКО ссылки (<a>), чтобы не конфликтовать с `.is-active` у компонентов.
   */
  updateActiveLinks() {
    const normalizePath = pathname => {
      let p = pathname || '/';
      try {
        p = decodeURI(p);
      } catch {
        // ignore decode errors
      }
      // /index.html -> /
      p = p.replace(/\/index\.html?$/i, '/');
      // trim trailing slash (except root)
      if (p.length > 1) p = p.replace(/\/+$/g, '');
      return p || '/';
    };

    const currentUrl = new URL(window.location.href);
    const currentPath = normalizePath(currentUrl.pathname);

    // Снимаем класс только со ссылок
    document.querySelectorAll('a.is-active[href]').forEach(a => a.classList.remove('is-active'));

    document.querySelectorAll('a[href]').forEach(a => {
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:'))
        return;

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      // Только тот же origin
      if (url.origin !== currentUrl.origin) return;

      const linkPath = normalizePath(url.pathname);
      if (linkPath === currentPath) {
        a.classList.add('is-active');
      }
    });
  }

  _getSwupPlugins() {
    // const rules = [
    //   {
    //     from: `/events(.*)`,
    //     to: `/events(.*)`,
    //     containers: ['#listing'],
    //     name: 'listing-update'
    //   },
    //   {
    //     from: `/blog(.*)`,
    //     to: `/blog(.*)`,
    //     containers: ['#listing'],
    //     name: 'listing-update'
    //   },
    //   {
    //     from: `/drake-art(.*)`,
    //     to: `/drake-art(.*)`,
    //     containers: ['#listing'],
    //     name: 'listing-update'
    //   }
    // ];

    const plugins = [
      new SwupParallelPlugin(),
      new SwupPreloadPlugin({
        preloadHoveredLinks: true
      }),
      new SwupBodyClassPlugin({
        prefix: 'u-'
      })
      // new SwupFragmentPlugin({
      //   rules: rules,
      //   debug: true
      // })
    ];

    return plugins;
  }

  // =============================================================================
  // Hooks
  // =============================================================================

  /**
   * On `transition.goTo` custom event
   * Navigate to a new page
   *
   * @param e
   */
  goTo(e) {
    this.swup.navigate(e.detail);
  }

  /**
   * On visit:start
   * Transition to a new page begins
   *
   * @see https://swup.js.org/hooks/#visit-start
   * @param visit
   */
  onVisitStart(visit) {
    if (!visit.fragmentVisit) {
      // Пусть Swup сбрасывает scroll (мы дополнительно форсим reset сразу после content:replace)
      visit.scroll.reset = false;
      $html.classList.add(CSS_CLASS.TRANSITION);
      $html.classList.remove(CSS_CLASS.READY);
    }

    // if (visit.fragmentVisit) {
    //   if (visit.fragmentVisit.name == 'listing-update') {
    //     Scroll.scrollTo('#listing', { offset: -200 });
    //   }
    // }

    // Dispatch custom event
    window.dispatchEvent(
      new CustomEvent(CUSTOM_EVENT.VISIT_START, {
        detail: visit
      })
    );
  }

  /**
   * On before:content:replace
   * The old content of the page is replaced by the new content.
   *
   * @see https://swup.js.org/hooks/#content-replace
   * @param visit
   */
  beforeContentReplace(visit) {
    if (!visit.fragmentVisit) {
      Scroll?.destroy();
      CardSlider?.destroy();
      PlaylistProximity?.destroy();
    }
  }

  /**
   * On content:replace
   * The old content of the page is replaced by the new content.
   *
   * @see https://swup.js.org/hooks/#content-replace
   * @param visit
   */
  onContentReplace(visit) {
    // const adminBar = document.getElementById('wpadminbar');
    // if (adminBar) {
    //   adminBar.remove();
    //   var parser = new DOMParser();
    //   var doc = parser.parseFromString(visit.to.html, 'text/html');
    //   var newAdminBar = doc.getElementById('wpadminbar');
    //   newAdminBar && document.body.appendChild(newAdminBar);
    // }

    if (!visit.fragmentVisit) {
      Scroll?.init();
      // ВАЖНО: сразу сбрасываем скролл, чтобы новая страница не появлялась на позиции старой
      // Scroll?.resetToTop?.();
      this.updateDocumentAttributes(visit);
      this.updateActiveLinks();

      // Обновляем SectionLinks после замены контента
      SectionLinks?.update();

      // Переинициализация компонентов на новой странице
      PlaylistProximity?.init();
      CardSlider?.init();
    }
  }

  /**
   * On animation:out:start
   * Current content starts animating out. Class `.is-animating` is added.
   *
   * @see https://swup.js.org/hooks/#animation-out-start
   * @param visit
   */
  onAnimationOutStart(visit) {
    if (!visit.fragmentVisit) {
      const scrollY = window.scrollY;
      $html.style.setProperty('--scroll-y', `${scrollY}px`);

      const transformOrigin = scrollY + window.innerHeight / 2;
      $html.style.setProperty('--transform-origin', `50% ${transformOrigin}px`);
    }
  }

  /**
   * On animation:in:end
   * New content finishes animating out.
   *
   * @see https://swup.js.org/hooks/#animation-in-end
   * @param visit
   */
  onAnimationInEnd(visit) {
    if (!visit.fragmentVisit) {
      window.scrollTo(0, 0);
      $html.classList.remove(CSS_CLASS.TRANSITION);
      $html.classList.add(CSS_CLASS.READY);
    }
  }
}
